export type QuestionnaireFieldType = 'short_text' | 'long_text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'checkbox';
export type QuestionnaireConditionOperator = 'equals' | 'not_equals' | 'is_truthy' | 'is_falsy' | 'contains';

export type QuestionnaireShowWhen = {
  questionKey: string;
  operator: QuestionnaireConditionOperator;
  value?: string;
};

export type QuestionnaireFieldValidation = {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
  patternMessage?: string;
};

export type QuestionnaireField = {
  key: string;
  label: string;
  type: QuestionnaireFieldType;
  required?: boolean;
  placeholder?: string | null;
  helpText?: string | null;
  options?: string[];
  defaultValue?: unknown;
  validation?: QuestionnaireFieldValidation;
  showWhen?: QuestionnaireShowWhen;
};

export type QuestionnairePage = {
  key: string;
  title: string;
  description?: string | null;
  showWhen?: QuestionnaireShowWhen;
  questions: QuestionnaireField[];
};

export type QuestionnaireTemplateDefinition = {
  title: string;
  description?: string | null;
  submitLabel?: string | null;
  questions: QuestionnaireField[];
  pages?: QuestionnairePage[];
};

export type QuestionnaireTemplatePreset = {
  id: string;
  name: string;
  description: string;
  definition: QuestionnaireTemplateDefinition;
};

export const QUESTIONNAIRE_FIELD_TYPES: QuestionnaireFieldType[] = ['short_text', 'long_text', 'email', 'phone', 'number', 'date', 'select', 'checkbox'];

export const QUESTIONNAIRE_CONDITION_OPERATORS: QuestionnaireConditionOperator[] = ['equals', 'not_equals', 'is_truthy', 'is_falsy', 'contains'];

function cloneQuestion(question: QuestionnaireField): QuestionnaireField {
  return {
    ...question,
    options: question.options ? [...question.options] : [],
    validation: question.validation ? { ...question.validation } : {},
    showWhen: question.showWhen ? { ...question.showWhen } : undefined,
  };
}

function clonePage(page: QuestionnairePage): QuestionnairePage {
  return {
    ...page,
    description: page.description || '',
    showWhen: page.showWhen ? { ...page.showWhen } : undefined,
    questions: page.questions.map(cloneQuestion),
  };
}

export function createQuestionnaireField(index = 0): QuestionnaireField {
  return {
    key: `field_${index + 1}`,
    label: 'New question',
    type: 'short_text',
    required: false,
    placeholder: '',
    helpText: '',
    options: [],
    validation: {},
  };
}

export function createQuestionnairePage(index = 0): QuestionnairePage {
  return {
    key: `page_${index + 1}`,
    title: `Page ${index + 1}`,
    description: '',
    questions: [createQuestionnaireField(0)],
  };
}

function createDefinitionFromPages(input: {
  title: string;
  description?: string | null;
  submitLabel?: string | null;
  pages: QuestionnairePage[];
}): QuestionnaireTemplateDefinition {
  const pages = input.pages.map(clonePage);
  return {
    title: input.title,
    description: input.description || '',
    submitLabel: input.submitLabel || '',
    pages,
    questions: pages.flatMap((page) => page.questions.map(cloneQuestion)),
  };
}

function buildDefaultPages(): QuestionnairePage[] {
  return [
    {
      key: 'clinic_basics',
      title: 'Clinic basics',
      description: 'Core identity and operating profile.',
      questions: [
        {
          key: 'clinicLegalName',
          label: 'Legal clinic name',
          type: 'short_text',
          required: true,
          placeholder: 'North Shore Family Clinic',
          validation: {
            minLength: 2,
            maxLength: 160,
          },
        },
        {
          key: 'clinicType',
          label: 'Clinic type',
          type: 'select',
          required: true,
          placeholder: 'Select a clinic type',
          options: ['Family Medicine', 'Specialist Clinic', 'Multi-specialty', 'Private Clinic', 'Hospital / Outpatient', 'Telemedicine-first'],
        },
        {
          key: 'doctorCount',
          label: 'How many doctors work at your clinic?',
          type: 'select',
          required: true,
          placeholder: 'Select a range',
          options: ['1', '2-5', '6-10', '10+'],
        },
        {
          key: 'locationCount',
          label: 'How many locations do you operate?',
          type: 'select',
          required: true,
          placeholder: 'Select a range',
          options: ['1', '2-3', '4+'],
        },
        {
          key: 'averagePatientsPerDay',
          label: 'Average patients per day',
          type: 'select',
          required: true,
          placeholder: 'Select a range',
          options: ['0-20', '20-50', '50-100', '100+'],
        },
        {
          key: 'preferredSubdomain',
          label: 'Preferred subdomain',
          type: 'short_text',
          required: true,
          placeholder: 'northshore',
          helpText: 'This becomes the tenant subdomain once the prospect is converted.',
          validation: {
            minLength: 2,
            maxLength: 63,
            pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$',
            patternMessage: 'Use lowercase letters, numbers, and hyphens only.',
          },
        },
        {
          key: 'country',
          label: 'Country',
          type: 'short_text',
          placeholder: 'CA',
          validation: {
            minLength: 2,
            maxLength: 80,
          },
        },
        {
          key: 'timezone',
          label: 'Timezone',
          type: 'short_text',
          required: true,
          placeholder: 'America/Toronto',
          validation: {
            minLength: 2,
            maxLength: 80,
          },
        },
      ],
    },
    {
      key: 'current_system',
      title: 'Current system',
      description: 'What you use today and where the pain points are.',
      questions: [
        {
          key: 'currentEmr',
          label: 'Do you currently use an EMR?',
          type: 'select',
          required: true,
          options: ['Yes (modern cloud EMR)', 'Yes (legacy system)', 'Partially (paper + digital)', 'No'],
        },
        {
          key: 'currentSystemIssue',
          label: 'Biggest issue with your current system',
          type: 'select',
          required: true,
          options: ['Slow', 'Poor UI', 'Lack of integrations', 'No automation', 'Weak reporting', 'Billing issues'],
        },
        {
          key: 'migrationNeed',
          label: 'Do you need data migration?',
          type: 'select',
          required: true,
          options: ['Full', 'Partial', 'No'],
        },
        {
          key: 'migrationDetails',
          label: 'What data needs to move?',
          type: 'long_text',
          placeholder: 'Patients, appointments, billing, documents, custom fields, etc.',
          showWhen: {
            questionKey: 'migrationNeed',
            operator: 'not_equals',
            value: 'No',
          },
          validation: {
            maxLength: 4000,
          },
        },
      ],
    },
    {
      key: 'scheduling',
      title: 'Scheduling and appointments',
      description: 'How appointments are managed and optimized.',
      questions: [
        {
          key: 'appointmentManagement',
          label: 'How do you manage appointments?',
          type: 'select',
          required: true,
          options: ['Phone only', 'Receptionist + system', 'Online booking', 'Mixed'],
        },
        {
          key: 'schedulingType',
          label: 'Scheduling type',
          type: 'select',
          required: true,
          options: ['Fixed time slots', 'Flexible', 'Hybrid', 'No-show focused'],
        },
        {
          key: 'noShowRate',
          label: 'No-show rate',
          type: 'select',
          required: true,
          options: ['<5%', '5-10%', '10-20%', '20%+'],
        },
        {
          key: 'aiSchedulingOptimization',
          label: 'Interested in AI scheduling optimization?',
          type: 'select',
          required: true,
          options: ['Yes (automatic)', 'Yes (assisted)', 'No'],
        },
        {
          key: 'requestedGoLiveDate',
          label: 'Requested go-live date',
          type: 'date',
        },
      ],
    },
    {
      key: 'clinical_workflow',
      title: 'Clinical workflow',
      description: 'How notes, coding, and visits are handled today.',
      questions: [
        {
          key: 'consultationDocumentation',
          label: 'How are consultations documented?',
          type: 'select',
          required: true,
          options: ['Typed notes', 'Templates', 'Dictation', 'Paper'],
        },
        {
          key: 'aiGeneratedNotesInterest',
          label: 'Interest in AI-generated notes',
          type: 'select',
          required: true,
          options: ['High', 'Medium', 'Low'],
        },
        {
          key: 'structuredCoding',
          label: 'Do you use structured coding (ICD/SNOMED)?',
          type: 'select',
          required: true,
          options: ['Yes', 'Partial', 'No'],
        },
        {
          key: 'avgConsultationTime',
          label: 'Average consultation time',
          type: 'select',
          required: true,
          options: ['5-10 min', '10-20 min', '20-30 min', '30+ min'],
        },
      ],
    },
    {
      key: 'telemedicine',
      title: 'Telemedicine',
      description: 'Virtual care maturity and AI triage needs.',
      questions: [
        {
          key: 'telemedicineOffer',
          label: 'Do you offer telemedicine?',
          type: 'select',
          required: true,
          options: ['Fully integrated', 'External tools', 'Planning to', 'No'],
        },
        {
          key: 'telemedicineUsage',
          label: 'Telemedicine usage',
          type: 'select',
          required: true,
          options: ['<10%', '10-30%', '30-60%', '60%+'],
        },
        {
          key: 'aiTriageNeed',
          label: 'Need AI triage / prioritization?',
          type: 'select',
          required: true,
          options: ['Yes', 'Maybe', 'No'],
        },
      ],
    },
    {
      key: 'patient_portal',
      title: 'Patient portal',
      description: 'What patients should be able to do and what remote care you need.',
      questions: [
        {
          key: 'patientRecordsOnline',
          label: 'Do patients access records online?',
          type: 'select',
          required: true,
          options: ['Yes', 'Limited', 'No'],
        },
        {
          key: 'patientPortalFeatures',
          label: 'What should patients be able to do?',
          type: 'select',
          required: true,
          options: ['Book appointments', 'View records', 'Messaging', 'Payments', 'All of the above'],
        },
        {
          key: 'remoteMonitoring',
          label: 'Do you want remote monitoring?',
          type: 'select',
          required: true,
          options: ['Yes (device integration)', 'Yes (manual input)', 'No'],
        },
      ],
    },
    {
      key: 'labs_and_imaging',
      title: 'Labs and imaging',
      description: 'How you receive and exchange diagnostic data.',
      questions: [
        {
          key: 'labResultHandling',
          label: 'How do you receive lab results?',
          type: 'select',
          required: true,
          options: ['Manual upload', 'Email/PDF', 'Integrated', 'Paper'],
        },
        {
          key: 'needLabIntegrations',
          label: 'Need lab integrations?',
          type: 'select',
          required: true,
          options: ['Yes (real-time)', 'Yes (basic)', 'No'],
        },
        {
          key: 'imagingWorkflow',
          label: 'Imaging workflow',
          type: 'select',
          required: true,
          options: ['External PACS', 'Integrated PACS', 'Manual'],
        },
      ],
    },
    {
      key: 'billing_and_payments',
      title: 'Billing and payments',
      description: 'How billing is done and what payment options you need.',
      questions: [
        {
          key: 'billingModel',
          label: 'Billing model',
          type: 'select',
          required: true,
          options: ['Private', 'Insurance', 'Government', 'Mixed'],
        },
        {
          key: 'billingAutomation',
          label: 'Need billing automation?',
          type: 'select',
          required: true,
          options: ['Full', 'Partial', 'No'],
        },
        {
          key: 'paymentMethods',
          label: 'Payment methods',
          type: 'select',
          required: true,
          options: ['In-person only', 'Online', 'Both'],
        },
      ],
    },
    {
      key: 'communication',
      title: 'Communication',
      description: 'Patient communication and internal coordination.',
      questions: [
        {
          key: 'patientCommunicationChannels',
          label: 'How do you communicate with patients?',
          type: 'select',
          required: true,
          options: ['Phone', 'Email', 'SMS', 'Patient portal', 'All'],
        },
        {
          key: 'internalMessaging',
          label: 'Need internal messaging between doctors?',
          type: 'select',
          required: true,
          options: ['Yes', 'No'],
        },
        {
          key: 'automatedReminders',
          label: 'Automated reminders',
          type: 'select',
          required: true,
          options: ['SMS', 'Email', 'Both', 'No'],
        },
      ],
    },
    {
      key: 'security_and_compliance',
      title: 'Security and compliance',
      description: 'Regulatory expectations and access control.',
      questions: [
        {
          key: 'requiresHipaa',
          label: 'Requires HIPAA',
          type: 'checkbox',
        },
        {
          key: 'requiresGdpr',
          label: 'Requires GDPR',
          type: 'checkbox',
        },
        {
          key: 'requiresPipeda',
          label: 'Requires PIPEDA',
          type: 'checkbox',
        },
        {
          key: 'requiresLocalRegulations',
          label: 'Other local regulations',
          type: 'checkbox',
        },
        {
          key: 'accessControlLevel',
          label: 'Access control level',
          type: 'select',
          required: true,
          options: ['Basic', 'Role-based', 'Advanced'],
        },
      ],
    },
    {
      key: 'reporting_and_analytics',
      title: 'Reporting and analytics',
      description: 'How often you use reports and what matters most.',
      questions: [
        {
          key: 'reportFrequency',
          label: 'How often do you use reports?',
          type: 'select',
          required: true,
          options: ['Daily', 'Weekly', 'Monthly', 'Rarely'],
        },
        {
          key: 'importantMetrics',
          label: 'Most important metrics',
          type: 'select',
          required: true,
          options: ['Revenue', 'Patient flow', 'Doctor performance', 'Clinical outcomes', 'All'],
        },
        {
          key: 'aiInsightsInterest',
          label: 'Interest in AI insights',
          type: 'select',
          required: true,
          options: ['High', 'Medium', 'Low'],
        },
      ],
    },
    {
      key: 'customization_and_integrations',
      title: 'Customization and integrations',
      description: 'Workflow changes, integration scope, and implementation priority.',
      questions: [
        {
          key: 'customWorkflows',
          label: 'Need custom workflows?',
          type: 'select',
          required: true,
          options: ['Critical', 'Minor', 'No'],
        },
        {
          key: 'requiredIntegrations',
          label: 'Required integrations',
          type: 'select',
          required: true,
          options: ['Insurance', 'Labs', 'Pharmacy', 'Accounting', 'All'],
        },
        {
          key: 'implementationPriority',
          label: 'Implementation priority',
          type: 'select',
          required: true,
          options: ['Fast go-live', 'Customization', 'Balanced'],
        },
        {
          key: 'implementationNotes',
          label: 'Implementation notes',
          type: 'long_text',
          placeholder: 'Anything else that matters for rollout planning.',
          validation: {
            maxLength: 4000,
          },
        },
      ],
    },
    {
      key: 'contact_and_notes',
      title: 'Contact and notes',
      description: 'Billing contacts and any remaining comments.',
      questions: [
        {
          key: 'billingContactName',
          label: 'Billing contact',
          type: 'short_text',
          placeholder: 'Alex Morgan',
          validation: {
            minLength: 2,
            maxLength: 120,
          },
        },
        {
          key: 'billingEmail',
          label: 'Billing email',
          type: 'email',
          placeholder: 'billing@clinic.com',
          validation: {
            minLength: 5,
            maxLength: 160,
          },
        },
        {
          key: 'notes',
          label: 'Additional notes',
          type: 'long_text',
          placeholder: 'Anything else the onboarding team should know.',
          validation: {
            maxLength: 4000,
          },
        },
      ],
    },
  ];
}

export const DEFAULT_QUESTIONNAIRE_TEMPLATE: QuestionnaireTemplateDefinition = createDefinitionFromPages({
  title: 'Elen1 clinic onboarding questionnaire',
  description: 'Capture clinic details needed to provision the organization and prepare a successful go-live.',
  submitLabel: 'Submit questionnaire',
  pages: buildDefaultPages(),
});

export const QUESTIONNAIRE_TEMPLATE_PRESETS: QuestionnaireTemplatePreset[] = [
  {
    id: 'default',
    name: 'Prospect onboarding',
    description: 'Balanced intake for standard prospect conversion.',
    definition: DEFAULT_QUESTIONNAIRE_TEMPLATE,
  },
  {
    id: 'primary_care',
    name: 'Primary care',
    description: 'Focused on clinic setup and patient operations for a standard practice.',
    definition: createDefinitionFromPages({
      title: 'Primary care onboarding questionnaire',
      description: 'Collect the baseline details for a primary care clinic.',
      submitLabel: 'Send answers',
      pages: [
        ...buildDefaultPages(),
        {
          key: 'workflow',
          title: 'Workflow',
          description: 'Front desk and care delivery details.',
          questions: [
            {
              key: 'appointmentVolume',
              label: 'Average daily appointment volume',
              type: 'number',
              validation: { minValue: 1, maxValue: 50000 },
            },
            {
              key: 'usesPortal',
              label: 'Uses a patient portal',
              type: 'checkbox',
            },
            {
              key: 'portalVendor',
              label: 'Portal vendor',
              type: 'short_text',
              placeholder: 'Vendor name',
              showWhen: { questionKey: 'usesPortal', operator: 'equals', value: 'true' },
            },
          ],
        },
      ],
    }),
  },
  {
    id: 'specialty',
    name: 'Specialty clinic',
    description: 'Adds room for specialty-specific setup details and go-live planning.',
    definition: createDefinitionFromPages({
      title: 'Specialty clinic onboarding questionnaire',
      description: 'Collect specialty workflow, billing, and go-live details.',
      submitLabel: 'Send answers',
      pages: [
        ...buildDefaultPages(),
        {
          key: 'specialty_workflow',
          title: 'Specialty workflow',
          description: 'Specialty-specific rollout details.',
          questions: [
            {
              key: 'specialtyWorkflowNotes',
              label: 'Specialty workflow notes',
              type: 'long_text',
              placeholder: 'Describe the specialty workflow, referral patterns, and intake needs.',
              validation: { maxLength: 4000 },
            },
            {
              key: 'supportsTelemed',
              label: 'Uses telemedicine',
              type: 'checkbox',
              helpText: 'Include telemed workflows and remote visit setup.',
            },
            {
              key: 'telemedVendor',
              label: 'Telemed vendor',
              type: 'short_text',
              placeholder: 'Zoom, Doxy.me, custom platform',
              showWhen: {
                questionKey: 'supportsTelemed',
                operator: 'equals',
                value: 'true',
              },
            },
          ],
        },
      ],
    }),
  },
  {
    id: 'multi_location',
    name: 'Multi-location rollout',
    description: 'Focused on organizations migrating more than one site.',
    definition: createDefinitionFromPages({
      title: 'Multi-location onboarding questionnaire',
      description: 'Capture rollout details for larger clinics with multiple locations.',
      submitLabel: 'Submit rollout details',
      pages: [
        ...buildDefaultPages(),
        {
          key: 'rollout',
          title: 'Rollout planning',
          description: 'Coverage for multi-site launches.',
          questions: [
            {
              key: 'locationCount',
              label: 'Number of locations',
              type: 'number',
              required: true,
              validation: { minValue: 1, maxValue: 250 },
            },
            {
              key: 'primaryLocationName',
              label: 'Primary location name',
              type: 'short_text',
              required: true,
              showWhen: {
                questionKey: 'locationCount',
                operator: 'not_equals',
                value: '1',
              },
            },
            {
              key: 'migrationPriority',
              label: 'Migration priority',
              type: 'select',
              required: true,
              options: ['Billing first', 'Scheduling first', 'Clinical data first', 'Full migration'],
            },
          ],
        },
      ],
    }),
  },
  {
    id: 'enterprise',
    name: 'Enterprise rollout',
    description: 'Broader intake for larger deployments and implementation teams.',
    definition: createDefinitionFromPages({
      title: 'Enterprise onboarding questionnaire',
      description: 'Collect more structured rollout and project planning details.',
      submitLabel: 'Submit rollout plan',
      pages: [
        ...buildDefaultPages(),
        {
          key: 'stakeholders',
          title: 'Stakeholders',
          description: 'Who is involved in the rollout and approval process.',
          questions: [
            {
              key: 'projectSponsor',
              label: 'Executive sponsor',
              type: 'short_text',
              required: true,
            },
            {
              key: 'implementationLead',
              label: 'Implementation lead',
              type: 'short_text',
              required: true,
            },
            {
              key: 'governanceReview',
              label: 'Needs governance review',
              type: 'checkbox',
            },
          ],
        },
        {
          key: 'dependencies',
          title: 'Dependencies',
          description: 'Track external and internal rollout constraints.',
          questions: [
            {
              key: 'dependenciesDescription',
              label: 'Dependencies or blockers',
              type: 'long_text',
              placeholder: 'List integrations, approvals, or deadlines that matter.',
              showWhen: {
                questionKey: 'governanceReview',
                operator: 'equals',
                value: 'true',
              },
              validation: { maxLength: 4000 },
            },
            {
              key: 'integrationVendor',
              label: 'Integration vendor',
              type: 'short_text',
              placeholder: 'Vendor or platform name',
            },
          ],
        },
      ],
    }),
  },
];

export function getQuestionnairePages(definition: QuestionnaireTemplateDefinition): QuestionnairePage[] {
  if (definition.pages?.length) {
    return definition.pages.map(clonePage);
  }

  return [
    {
      key: 'page_1',
      title: definition.title,
      description: definition.description || '',
      questions: definition.questions.map(cloneQuestion),
    },
  ];
}

export function getQuestionnaireQuestions(definition: QuestionnaireTemplateDefinition): QuestionnaireField[] {
  return getQuestionnairePages(definition).flatMap((page) => page.questions.map(cloneQuestion));
}

export function createQuestionnaireTemplateDefinition(): QuestionnaireTemplateDefinition {
  return cloneQuestionnaireTemplateDefinition(DEFAULT_QUESTIONNAIRE_TEMPLATE);
}

export function cloneQuestionnaireTemplateDefinition(definition: QuestionnaireTemplateDefinition): QuestionnaireTemplateDefinition {
  const pages = getQuestionnairePages(definition);
  return {
    title: definition.title,
    description: definition.description || '',
    submitLabel: definition.submitLabel || '',
    pages,
    questions: pages.flatMap((page) => page.questions.map(cloneQuestion)),
  };
}

function normalizeQuestionAnswer(value: unknown) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

export function isQuestionVisible(question: QuestionnaireField, answers: Record<string, unknown>) {
  if (!question.showWhen) return true;
  const otherValue = answers[question.showWhen.questionKey];
  if (otherValue === null || otherValue === undefined || otherValue === '') return false;

  const actual = normalizeQuestionAnswer(otherValue);
  const expected = question.showWhen.value ?? '';

  switch (question.showWhen.operator) {
    case 'equals':
      return actual === expected;
    case 'not_equals':
      return actual !== expected;
    case 'contains':
      return actual.toLowerCase().includes(expected.toLowerCase());
    case 'is_truthy':
      return ['true', '1', 'yes', 'on'].includes(actual.toLowerCase());
    case 'is_falsy':
      return !['true', '1', 'yes', 'on'].includes(actual.toLowerCase());
    default:
      return true;
  }
}

export function isQuestionnairePageVisible(page: QuestionnairePage, answers: Record<string, unknown>) {
  if (!page.showWhen) return true;
  const otherValue = answers[page.showWhen.questionKey];
  if (otherValue === null || otherValue === undefined || otherValue === '') return false;

  const actual = normalizeQuestionAnswer(otherValue);
  const expected = page.showWhen.value ?? '';

  switch (page.showWhen.operator) {
    case 'equals':
      return actual === expected;
    case 'not_equals':
      return actual !== expected;
    case 'contains':
      return actual.toLowerCase().includes(expected.toLowerCase());
    case 'is_truthy':
      return ['true', '1', 'yes', 'on'].includes(actual.toLowerCase());
    case 'is_falsy':
      return !['true', '1', 'yes', 'on'].includes(actual.toLowerCase());
    default:
      return true;
  }
}
